import { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import ProposalList from "./ProposalList";
import ProposalForm from "./ProposalForm";
import AlertDialog from "./AlertDialog";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import {
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  ArrowLeftIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  BanknotesIcon,
} from "@heroicons/react/20/solid";
import { useLocation } from "react-router-dom";
import SharePointFilesTab from "./OpportunitySharePointFilesTab";

const API = "https://erp-xe9u.onrender.com/api";

// NOTE: restrict this key by HTTP referrer in Google Cloud, or move it to an env var.
const GOOGLE_PLACES_KEY = "AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg";

const ESTIMATOR_ROLES = ["admin", "project_manager"];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const STATUS_OPTIONS = [
  { value: "Undecided", label: "Undecided" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
  { value: "No Opportunity", label: "No Opportunity" },
];

const ESTIMATION_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const CONSTRUCTION_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "Renovation", label: "Renovation" },
  { value: "New Build", label: "New Build" },
  { value: "Mixed", label: "Mixed (Renovation & New Build)" },
];

const MARKET_SEGMENT_OPTIONS = [
  { value: "", label: "None" },
  { value: "Commercial", label: "Commercial" },
  { value: "Institutional", label: "Institutional" },
  {
    value: "Multi-Residential/High-Rise",
    label: "Multi-Residential/High-Rise",
  },
  { value: "Industrial", label: "Industrial" },
  { value: "Other", label: "Other" },
];

const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const EMPTY_ADDRESS = {
  formattedAddress: "",
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
  placeId: "",
};

const inputClass =
  "block w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors";

const btnDanger =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-400 ring-1 ring-red-500/20 hover:bg-red-500/20 transition-colors";

const formatDate = (date) =>
  date ? new Date(date).toISOString().split("T")[0] : "N/A";

const formatDateForInput = (date) =>
  date ? new Date(date).toISOString().slice(0, 10) : "";

const formatCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const statusTone = (s) =>
  s === "Won"
    ? "green"
    : s === "Lost"
      ? "red"
      : s === "No Opportunity"
        ? "gray"
        : "yellow";

const estimationTone = (s) =>
  s === "completed" ? "green" : s === "in_progress" ? "yellow" : "gray";

function Section({ title, children, className = "" }) {
  return (
    <section
      className={classNames(
        "rounded-xl bg-gray-800 ring-1 ring-white/10",
        className,
      )}
    >
      {title && (
        <div className="border-b border-white/10 px-5 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-400">
            {title}
          </h3>
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </label>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-100 break-words">{children}</dd>
    </div>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-800 px-4 py-3 ring-1 ring-white/10">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-400">
        <Icon className="size-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-100">{value}</p>
      </div>
    </div>
  );
}

function ActivityFields({ value, onChange, userOptions, showStatus = false }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField label="Title">
        <input
          type="text"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className={inputClass}
          placeholder="Enter activity title"
        />
      </FormField>
      <FormField label="Due Date">
        <input
          type="datetime-local"
          value={value.dueDate}
          onChange={(e) => onChange({ ...value, dueDate: e.target.value })}
          className={inputClass}
        />
      </FormField>
      <FormField label="Description" className="md:col-span-2">
        <textarea
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          className={inputClass}
          rows={4}
          placeholder="Enter activity description"
        />
      </FormField>
      <FormField label="Method">
        <select
          value={value.method}
          onChange={(e) => onChange({ ...value, method: e.target.value })}
          className={inputClass}
        >
          <option value="email">Email</option>
          <option value="call">Call</option>
          <option value="in-person">In-Person</option>
        </select>
      </FormField>
      {showStatus && (
        <FormField label="Status">
          <select
            value={value.status}
            onChange={(e) => onChange({ ...value, status: e.target.value })}
            className={inputClass}
          >
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </FormField>
      )}
      <FormField label="Assigned To">
        <Select
          options={userOptions}
          value={userOptions.find((o) => o.value === value.assignedTo) || null}
          onChange={(selected) =>
            onChange({ ...value, assignedTo: selected ? selected.value : null })
          }
          placeholder="Select user"
          styles={customSelectStyles}
        />
      </FormField>
    </div>
  );
}

export default function OpportunityDetails({
  opportunity,
  proposals,
  setProposals,
  setOpportunities,
  users,
  accounts,
  contacts,
  companyProfile,
  userEmail,
  navigate,
}) {
  const [activeSubTab, setActiveSubTab] = useState("General");

  const location = useLocation();
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [isEditingProposal, setIsEditingProposal] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteSortOrder, setNoteSortOrder] = useState("desc");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [newActivity, setNewActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    assignedTo: null,
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [editActivity, setEditActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    status: "pending",
    assignedTo: null,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: "",
    status: "",
    estimationStatus: "",
    estimator: null,
    contact: null,
    account: null,
    bidders: [],
    source: "",
    estimateDueDate: "",
    estimatedRevenue: "",
    estimationCompletionDate: "",
    proposalSentDate: "",
    proposalSignedDate: "",
    drawingLinks: [],
    estimatedStartDate: "",
    sourceCategory: "",
    constructionType: "",
    marketSegment: "",
    jobAddress: { ...EMPTY_ADDRESS },
  });
  const [newBidder, setNewBidder] = useState(null);

  const tabs = [
    { name: "General", current: activeSubTab === "General" },
    { name: "Proposals", current: activeSubTab === "Proposals" },
    { name: "Notes", current: activeSubTab === "Notes" },
    { name: "Activities", current: activeSubTab === "Activities" },
    { name: "Files", current: activeSubTab === "Files" },
  ];

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));

  const showAlert = (type, title, message, extra = {}) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm: closeAlert,
      showCancel: false,
      ...extra,
    });

  const showError = (message) => showAlert("error", "Error", message);
  const showSuccess = (message) => showAlert("success", "Success", message);

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        const currentUser = users.find((user) => user.email === decoded.email);
        return currentUser?._id || null;
      }
      return users.find((user) => user.email === userEmail)?._id || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const userOptions = users.map((user) => ({
    value: user._id,
    label: user.displayName || user.email,
  }));

  // Estimators: admins + project managers (keep current estimator visible
  // even if their role changed, so existing records still render).
  const estimatorOptions = users
    .filter(
      (user) =>
        ESTIMATOR_ROLES.includes(user.role) ||
        user._id === opportunity?.estimator?._id,
    )
    .map((user) => ({
      value: user._id,
      label: user.displayName || user.email,
    }));

  const accountOptions = accounts.map((account) => ({
    value: account._id,
    label: account.companyName,
  }));

  const contactOptions = contacts.map((contact) => ({
    value: contact._id,
    label: `${contact.firstName} ${contact.lastName}`,
  }));

  useEffect(() => {
    const fetchLeadSources = async () => {
      try {
        const response = await axios.get(
          `${API}/company-profile`,
          authConfig(),
        );
        setLeadSources(
          response.data.plugins?.automatedLeads?.sources?.map(
            (s) => s.sourceName,
          ) || [],
        );
      } catch (err) {
        console.error("Error fetching company profile:", err);
      }
    };
    fetchLeadSources();

    if (opportunity) {
      let jobAddress;
      if (typeof opportunity.jobAddress === "string") {
        try {
          jobAddress = JSON.parse(opportunity.jobAddress);
        } catch (err) {
          console.error("Error parsing jobAddress:", err);
          jobAddress = { ...EMPTY_ADDRESS };
        }
      } else {
        jobAddress = {
          formattedAddress: opportunity.jobAddress?.formattedAddress || "",
          street: opportunity.jobAddress?.street || "",
          city: opportunity.jobAddress?.city || "",
          state: opportunity.jobAddress?.state || "",
          postalCode: opportunity.jobAddress?.postalCode || "",
          country: opportunity.jobAddress?.country || "",
          latitude: opportunity.jobAddress?.latitude || null,
          longitude: opportunity.jobAddress?.longitude || null,
          placeId: opportunity.jobAddress?.placeId || "",
        };
      }

      setEditFormData({
        title: opportunity.title || "",
        status:
          opportunity.status &&
          STATUS_OPTIONS.some((opt) => opt.value === opportunity.status)
            ? opportunity.status
            : "Undecided",
        estimationStatus:
          opportunity.estimationStatus &&
          ESTIMATION_STATUS_OPTIONS.some(
            (opt) => opt.value === opportunity.estimationStatus,
          )
            ? opportunity.estimationStatus
            : "pending",
        estimator: opportunity.estimator?._id || null,
        contact: opportunity.contact?._id || opportunity.contact || null,
        account: opportunity.account?._id || opportunity.account || null,
        bidders: opportunity.bidders?.map((b) => b._id) || [],
        sourceCategory: opportunity.sourceCategory || "",
        constructionType: opportunity.constructionType || "",
        marketSegment: opportunity.marketSegment || "",
        estimateDueDate: formatDateForInput(opportunity.estimateDueDate),
        estimatedRevenue: opportunity.estimatedRevenue || "",
        estimationCompletionDate: formatDateForInput(
          opportunity.estimationCompletionDate,
        ),
        proposalSentDate: formatDateForInput(opportunity.proposalSentDate),
        proposalSignedDate: formatDateForInput(opportunity.proposalSignedDate),
        drawingLinks: opportunity.drawingLinks || [],
        estimatedStartDate: formatDateForInput(opportunity.estimatedStartDate),
        jobAddress,
      });
    }

    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      setNewActivity((prev) => ({ ...prev, assignedTo: currentUserId }));
    }
  }, [opportunity, users]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const activityId = params.get("activityId");

    if (tab && tabs.some((t) => t.name === tab)) {
      setActiveSubTab(tab);
      if (tab === "Activities" && activityId && activities.length > 0) {
        const activity = activities.find((act) => act._id === activityId);
        if (activity) {
          setSelectedActivity(activity);
        }
      }
    }
  }, [location, activities]);

  const fetchProposals = async () => {
    if (!opportunity?._id) return showError("Invalid opportunity ID");
    try {
      const response = await axios.get(
        `${API}/proposals?opportunity=${opportunity._id}`,
        authConfig(),
      );
      setProposals(response.data);
    } catch (err) {
      showError(
        "Failed to fetch proposals: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const fetchTimeline = async () => {
    if (!opportunity?._id) return showError("Invalid opportunity ID");
    try {
      const response = await axios.get(
        `${API}/opportunities/${opportunity._id}/timeline`,
        authConfig(),
      );
      setTimelineEvents(response.data);
    } catch (err) {
      showError(
        "Failed to fetch timeline: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const fetchActivities = async () => {
    if (!opportunity?._id) return showError("Invalid opportunity ID");
    try {
      const response = await axios.get(
        `${API}/opportunities/${opportunity._id}/activities`,
        authConfig(),
      );
      setActivities(response.data.activities);
    } catch (err) {
      showError(
        "Failed to fetch activities: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  useEffect(() => {
    if (opportunity?._id) {
      fetchProposals();
      fetchTimeline();
      fetchActivities();
    }
  }, [opportunity?._id]);

  const handleAddressChange = async (place) => {
    if (!place || !place.value || !place.value.place_id) {
      return showError("Please select a valid address from the dropdown.");
    }

    try {
      const response = await axios.get(
        `${API}/opportunities/address/${place.value.place_id}`,
        authConfig(),
      );

      if (response.data.success) {
        const { address } = response.data;
        setEditFormData((prev) => ({
          ...prev,
          jobAddress: {
            formattedAddress: address.formattedAddress,
            street: address.street,
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
            country: address.country,
            latitude: address.latitude,
            longitude: address.longitude,
            placeId: place.value.place_id,
          },
        }));
      } else {
        throw new Error(
          response.data.message || "Failed to fetch address details",
        );
      }
    } catch (err) {
      showError(`Error fetching address details: ${err.message}.`);
      setEditFormData((prev) => ({
        ...prev,
        jobAddress: {
          ...prev.jobAddress,
          formattedAddress: place.label,
          placeId: place.value.place_id,
        },
      }));
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) return showError("Invalid opportunity ID");
    if (!noteContent.trim()) return showError("Note content cannot be empty.");

    try {
      const response = await axios.post(
        `${API}/opportunities/${opportunity._id}/notes`,
        { content: noteContent.trim() },
        authConfig(),
      );

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunity._id
            ? { ...opp, notes: [...(opp.notes || []), response.data.note] }
            : opp,
        ),
      );
      setNoteContent("");
      setShowNoteForm(false);
      showSuccess("Note saved successfully!");
      fetchTimeline();
    } catch (err) {
      showError(
        "Failed to save note: " + (err.response?.data?.message || err.message),
      );
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) return showError("Invalid opportunity ID");

    if (
      !newActivity.title.trim() ||
      !newActivity.dueDate ||
      !newActivity.description.trim() ||
      !newActivity.assignedTo
    ) {
      return showError(
        "Title, due date, description, and assigned user are required",
      );
    }

    try {
      const response = await axios.post(
        `${API}/opportunities/${opportunity._id}/activities`,
        newActivity,
        authConfig(),
      );

      setActivities((prev) => [...prev, response.data.activity]);
      setNewActivity({
        title: "",
        dueDate: "",
        description: "",
        method: "email",
        assignedTo: getCurrentUserId(),
      });
      setShowActivityForm(false);
      showSuccess("Activity added successfully!");
      fetchTimeline();
    } catch (err) {
      showError(
        "Failed to add activity: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleUpdateActivity = async (activityId, updates) => {
    try {
      const response = await axios.put(
        `${API}/opportunities/${opportunity._id}/activities/${activityId}`,
        updates,
        authConfig(),
      );

      setActivities((prev) =>
        prev.map((act) =>
          act._id === activityId ? response.data.activity : act,
        ),
      );
      setSelectedActivity(response.data.activity);
      setIsEditingActivity(false);
      showSuccess("Activity updated successfully!");
      fetchTimeline();
    } catch (err) {
      showError(
        "Failed to update activity: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleEditActivity = (activity) => {
    setIsEditingActivity(true);
    setEditActivity({
      title: activity.title,
      dueDate: new Date(activity.dueDate).toISOString().slice(0, 16),
      description: activity.description,
      method: activity.method,
      status: activity.status,
      assignedTo: activity.assignedTo?._id || activity.createdBy?._id,
    });
  };

  const handleSubmitEditActivity = async (e) => {
    e.preventDefault();
    if (
      !editActivity.title.trim() ||
      !editActivity.dueDate ||
      !editActivity.description.trim() ||
      !editActivity.assignedTo
    ) {
      return showError(
        "Title, due date, description, and assigned user are required",
      );
    }
    await handleUpdateActivity(selectedActivity._id, editActivity);
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await axios.delete(
        `${API}/opportunities/${opportunity._id}/activities/${activityId}`,
        authConfig(),
      );

      setActivities((prev) => prev.filter((act) => act._id !== activityId));
      setSelectedActivity(null);
      setIsEditingActivity(false);
      showSuccess("Activity deleted successfully!");
      fetchTimeline();
    } catch (err) {
      showError(
        "Failed to delete activity: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleUpdateOpportunity = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) return showError("Invalid opportunity ID");
    if (!editFormData.title.trim()) return showError("Title is required");
    if (!editFormData.jobAddress.formattedAddress) {
      return showError("Please select a valid job address.");
    }
    if (editFormData.status === "Won" && opportunity?.status !== "Won") {
      return showError(
        "Cannot set status to Won. Please use the 'Awarded' action to set the status and proposal signed date.",
      );
    }
    if (
      opportunity?.status === "Won" &&
      opportunity?.proposalSignedDate &&
      editFormData.status === "Undecided"
    ) {
      return showError(
        "Cannot change status to Undecided from Won. Please select Lost to clear the proposal signed date or keep the status as Won.",
      );
    }
    try {
      const { proposalSignedDate, ...payload } = editFormData;
      const response = await axios.put(
        `${API}/opportunities/${opportunity._id}`,
        {
          ...payload,
          drawingLinks: editFormData.drawingLinks.filter((link) => link.trim()),
          jobAddress: editFormData.jobAddress,
        },
        authConfig(),
      );

      const updatedOpp = response.data.opportunity || response.data;
      setOpportunities((prev) =>
        prev.map((opp) => (opp._id === opportunity._id ? updatedOpp : opp)),
      );
      setIsEditingGeneral(false);
      showSuccess("Opportunity updated successfully!");
      fetchTimeline();
    } catch (err) {
      showError(
        "Failed to update opportunity: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleAddDrawingLink = () => {
    setEditFormData({
      ...editFormData,
      drawingLinks: [...editFormData.drawingLinks, ""],
    });
  };

  const handleRemoveDrawingLink = (index) => {
    setEditFormData({
      ...editFormData,
      drawingLinks: editFormData.drawingLinks.filter((_, i) => i !== index),
    });
  };

  const handleDrawingLinkChange = (index, value) => {
    const updatedLinks = [...editFormData.drawingLinks];
    updatedLinks[index] = value;
    setEditFormData({ ...editFormData, drawingLinks: updatedLinks });
  };

  const setField = (key, value) =>
    setEditFormData((prev) => ({ ...prev, [key]: value }));

  const toggleNoteSortOrder = () => {
    setNoteSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const sortedNotes = opportunity?.notes
    ? [...opportunity.notes].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return noteSortOrder === "desc" ? dateB - dateA : dateA - dateB;
      })
    : [];

  const pendingActivities = activities
    .filter((act) => act.status === "pending")
    .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

  const getEventStatus = (event, index) => {
    const now = new Date();
    const eventDate = new Date(event.timestamp);
    if (eventDate <= now) {
      return index === 0 ? "current" : "complete";
    }
    return "upcoming";
  };

  if (!opportunity) {
    return (
      <div className="py-8 text-center text-red-400">Opportunity not found</div>
    );
  }

  const eventTitle = (event) =>
    `${event.action.charAt(0).toUpperCase() + event.action.slice(1)} ${event.entityType}`;

  return (
    <div className="mx-auto max-w-full">
      <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
              Opportunity
            </p>
            <h1 className="mt-1 truncate text-2xl font-bold text-white">
              {opportunity.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge tone={statusTone(opportunity.status)}>
                {opportunity.status}
              </Badge>
              <Badge tone={estimationTone(opportunity.estimationStatus)}>
                Estimation:{" "}
                {ESTIMATION_STATUS_OPTIONS.find(
                  (opt) => opt.value === opportunity.estimationStatus,
                )?.label || opportunity.estimationStatus}
              </Badge>
              {opportunity.account?.companyName && (
                <span className="text-sm text-gray-400">
                  {opportunity.account.companyName}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate("/Opportunities")}
            className={btnSecondary}
          >
            <ArrowLeftIcon className="size-4" />
            Back to Opportunities
          </button>
        </div>

        {/* Key metrics */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            icon={BanknotesIcon}
            label="Estimated Revenue"
            value={formatCurrency(opportunity.estimatedRevenue)}
          />
          <Stat
            icon={CalendarDaysIcon}
            label="Estimate Due"
            value={formatDate(opportunity.estimateDueDate)}
          />
          <Stat
            icon={CalendarDaysIcon}
            label="Proposal Sent"
            value={formatDate(opportunity.proposalSentDate)}
          />
          <Stat
            icon={UserCircleIcon}
            label="Estimator"
            value={opportunity.estimator?.displayName || "Unassigned"}
          />
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="grid grid-cols-1 sm:hidden">
            <select
              value={activeSubTab}
              onChange={(e) => setActiveSubTab(e.target.value)}
              aria-label="Select a tab"
              className="col-start-1 row-start-1 w-full appearance-none rounded-lg bg-gray-800 py-2 pr-8 pl-3 text-base font-semibold text-gray-200 outline-1 -outline-offset-1 outline-gray-700 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-400"
            >
              {tabs.map((tab) => (
                <option key={tab.name} value={tab.name}>
                  {tab.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-400"
            />
          </div>
          <div className="hidden sm:block">
            <nav
              aria-label="Tabs"
              className="flex gap-1 rounded-xl bg-gray-800 p-1 ring-1 ring-white/10"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveSubTab(tab.name)}
                  aria-current={tab.current ? "page" : undefined}
                  className={classNames(
                    tab.current
                      ? "bg-orange-500 text-white shadow"
                      : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                    "flex-1 rounded-lg px-4 py-2 text-center text-sm font-semibold transition-colors",
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* GENERAL */}
        {activeSubTab === "General" && (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-6 xl:col-span-2">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {isEditingGeneral ? "Edit Details" : "Details"}
                </h2>
                {!isEditingGeneral ? (
                  <button
                    onClick={() => setIsEditingGeneral(true)}
                    className={btnPrimary}
                  >
                    <PencilSquareIcon className="size-4" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditingGeneral(false)}
                      className={btnSecondary}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateOpportunity}
                      className={btnPrimary}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>

              {isEditingGeneral ? (
                <form onSubmit={handleUpdateOpportunity} className="space-y-6">
                  <Section title="Overview">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField label="Title" className="md:col-span-2">
                        <input
                          type="text"
                          value={editFormData.title}
                          onChange={(e) => setField("title", e.target.value)}
                          className={inputClass}
                          required
                        />
                      </FormField>
                      <FormField label="Status">
                        <Select
                          options={STATUS_OPTIONS}
                          value={
                            STATUS_OPTIONS.find(
                              (o) => o.value === editFormData.status,
                            ) || null
                          }
                          onChange={(s) => setField("status", s ? s.value : "")}
                          styles={customSelectStyles}
                          isClearable
                        />
                      </FormField>
                      <FormField label="Estimation Status">
                        <Select
                          options={ESTIMATION_STATUS_OPTIONS}
                          value={
                            ESTIMATION_STATUS_OPTIONS.find(
                              (o) => o.value === editFormData.estimationStatus,
                            ) || null
                          }
                          onChange={(s) =>
                            setField("estimationStatus", s ? s.value : "")
                          }
                          styles={customSelectStyles}
                          isClearable
                        />
                      </FormField>
                      <FormField label="Estimated Revenue">
                        <input
                          type="number"
                          step="0.01"
                          value={editFormData.estimatedRevenue}
                          onChange={(e) =>
                            setField("estimatedRevenue", e.target.value)
                          }
                          className={inputClass}
                        />
                      </FormField>
                    </div>
                  </Section>

                  <Section title="Team & Accounts">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField label="Estimator (Admin / PM)">
                        <Select
                          options={estimatorOptions}
                          value={
                            estimatorOptions.find(
                              (o) => o.value === editFormData.estimator,
                            ) || null
                          }
                          onChange={(s) =>
                            setField("estimator", s ? s.value : null)
                          }
                          styles={customSelectStyles}
                          placeholder="Select estimator..."
                          isClearable
                        />
                      </FormField>
                      <FormField label="Contact">
                        <Select
                          options={contactOptions}
                          value={
                            contactOptions.find(
                              (o) => o.value === editFormData.contact,
                            ) || null
                          }
                          onChange={(s) =>
                            setField("contact", s ? s.value : null)
                          }
                          styles={customSelectStyles}
                          isClearable
                        />
                      </FormField>
                      <FormField label="Primary Account">
                        <Select
                          options={accountOptions}
                          value={
                            accountOptions.find(
                              (o) => o.value === editFormData.account,
                            ) || null
                          }
                          onChange={(s) =>
                            setField("account", s ? s.value : null)
                          }
                          styles={customSelectStyles}
                          isClearable
                        />
                      </FormField>
                      <FormField label="Bidders">
                        <Select
                          options={accountOptions}
                          value={accountOptions.filter((o) =>
                            editFormData.bidders.includes(o.value),
                          )}
                          onChange={(s) =>
                            setField("bidders", s ? s.map((o) => o.value) : [])
                          }
                          styles={customSelectStyles}
                          isMulti
                          placeholder="Select bidders..."
                        />
                      </FormField>
                    </div>
                  </Section>

                  <Section title="Classification">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <FormField label="Lead Source">
                        <select
                          value={editFormData.sourceCategory}
                          onChange={(e) =>
                            setField("sourceCategory", e.target.value)
                          }
                          className={inputClass}
                        >
                          <option value="">Select a source...</option>
                          {LEAD_SOURCE_CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Construction Type">
                        <select
                          value={editFormData.constructionType}
                          onChange={(e) =>
                            setField("constructionType", e.target.value)
                          }
                          className={inputClass}
                        >
                          {CONSTRUCTION_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Market Segment">
                        <select
                          value={editFormData.marketSegment}
                          onChange={(e) =>
                            setField("marketSegment", e.target.value)
                          }
                          className={inputClass}
                        >
                          {MARKET_SEGMENT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                  </Section>

                  <Section title="Location">
                    <Label>Job Address</Label>
                    <GooglePlacesAutocomplete
                      autocompletionRequest={{
                        componentRestrictions: { country: ["ca"] },
                      }}
                      apiKey={GOOGLE_PLACES_KEY}
                      selectProps={{
                        onChange: handleAddressChange,
                        placeholder:
                          "Enter a job address (e.g., 123 Main St, Toronto, ON)",
                        value: editFormData.jobAddress.formattedAddress
                          ? {
                              label: editFormData.jobAddress.formattedAddress,
                              value: {
                                description:
                                  editFormData.jobAddress.formattedAddress,
                              },
                            }
                          : null,
                        styles: customSelectStyles,
                      }}
                    />
                  </Section>

                  <Section title="Key Dates">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField label="Estimate Due Date">
                        <input
                          type="date"
                          value={editFormData.estimateDueDate}
                          onChange={(e) =>
                            setField("estimateDueDate", e.target.value)
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Estimation Completion Date">
                        <input
                          type="date"
                          value={editFormData.estimationCompletionDate}
                          onChange={(e) =>
                            setField("estimationCompletionDate", e.target.value)
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Proposal Sent Date">
                        <input
                          type="date"
                          value={editFormData.proposalSentDate}
                          onChange={(e) =>
                            setField("proposalSentDate", e.target.value)
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Estimated Start Date">
                        <input
                          type="date"
                          value={editFormData.estimatedStartDate}
                          onChange={(e) =>
                            setField("estimatedStartDate", e.target.value)
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Proposal Signed Date">
                        <input
                          type="date"
                          value={editFormData.proposalSignedDate}
                          className={classNames(
                            inputClass,
                            "cursor-not-allowed opacity-60",
                          )}
                          disabled
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Set automatically when the opportunity is awarded.
                        </p>
                      </FormField>
                    </div>
                  </Section>

                  <Section title="Drawing Links">
                    <div className="space-y-2">
                      {editFormData.drawingLinks.map((link, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="url"
                            value={link}
                            onChange={(e) =>
                              handleDrawingLinkChange(index, e.target.value)
                            }
                            className={inputClass}
                            placeholder="Enter drawing link"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveDrawingLink(index)}
                            className="rounded-lg p-2 text-red-400 hover:bg-red-500/10"
                            aria-label="Remove link"
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddDrawingLink}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-gray-700 px-3 py-1.5 text-sm font-medium text-orange-400 hover:bg-gray-600"
                      >
                        <PlusIcon className="size-4" />
                        Add Drawing Link
                      </button>
                    </div>
                  </Section>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditingGeneral(false)}
                      className={btnSecondary}
                    >
                      Cancel
                    </button>
                    <button type="submit" className={btnPrimary}>
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <Section title="Overview">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                      <Field label="Title">{opportunity.title}</Field>
                      <Field label="Estimated Revenue">
                        {formatCurrency(opportunity.estimatedRevenue)}
                      </Field>
                      <Field label="Status">
                        <Badge tone={statusTone(opportunity.status)}>
                          {opportunity.status}
                        </Badge>
                      </Field>
                      <Field label="Estimation Status">
                        <Badge
                          tone={estimationTone(opportunity.estimationStatus)}
                        >
                          {ESTIMATION_STATUS_OPTIONS.find(
                            (opt) => opt.value === opportunity.estimationStatus,
                          )?.label || opportunity.estimationStatus}
                        </Badge>
                      </Field>
                    </dl>
                  </Section>

                  <Section title="Team & Accounts">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                      <Field label="Estimator">
                        {opportunity.estimator?.displayName || "N/A"}
                      </Field>
                      <Field label="Contact">
                        {opportunity.contact
                          ? `${opportunity.contact.firstName} ${opportunity.contact.lastName}`
                          : "N/A"}
                      </Field>
                      <Field label="Primary Account">
                        {opportunity.account?.companyName || "N/A"}
                      </Field>
                      <Field label="Bidders">
                        {opportunity.bidders &&
                        opportunity.bidders.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {opportunity.bidders.map((bidder) => (
                              <span
                                key={bidder._id}
                                className="rounded-md bg-gray-700 px-2 py-0.5 text-xs text-gray-200"
                              >
                                {bidder.companyName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">No bidders</span>
                        )}
                      </Field>
                    </dl>
                  </Section>

                  <Section title="Classification">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-3">
                      <Field label="Lead Source">
                        {opportunity.sourceCategory || "—"}
                      </Field>
                      <Field label="Construction Type">
                        {opportunity.constructionType || "—"}
                      </Field>
                      <Field label="Market Segment">
                        {opportunity.marketSegment || "—"}
                      </Field>
                    </dl>
                  </Section>

                  <Section title="Location">
                    <div className="flex items-start gap-2 text-sm text-gray-100">
                      <MapPinIcon className="mt-0.5 size-5 shrink-0 text-orange-400" />
                      <span>
                        {opportunity.jobAddress?.formattedAddress || "N/A"}
                      </span>
                    </div>
                  </Section>

                  <Section title="Key Dates">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                      <Field label="Estimate Due">
                        {formatDate(opportunity.estimateDueDate)}
                      </Field>
                      <Field label="Estimation Completed">
                        {formatDate(opportunity.estimationCompletionDate)}
                      </Field>
                      <Field label="Proposal Sent">
                        {formatDate(opportunity.proposalSentDate)}
                      </Field>
                      <Field label="Proposal Signed">
                        {formatDate(opportunity.proposalSignedDate)}
                      </Field>
                      <Field label="Estimated Start">
                        {formatDate(opportunity.estimatedStartDate)}
                      </Field>
                    </dl>
                  </Section>

                  <Section title="Drawing Links">
                    {opportunity.drawingLinks &&
                    opportunity.drawingLinks.length > 0 ? (
                      <ul className="space-y-1.5">
                        {opportunity.drawingLinks.map((link, idx) => (
                          <li key={idx} className="truncate text-sm">
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 hover:text-orange-300 hover:underline"
                            >
                              {link}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400">No drawing links</p>
                    )}
                  </Section>
                </div>
              )}

              {pendingActivities.length > 0 && (
                <Section title="Next Tasks">
                  <div className="flex flex-wrap gap-2">
                    {pendingActivities.map((activity) => {
                      const dueDate = new Date(activity.dueDate);
                      const now = new Date();
                      const isPastDue =
                        Date.UTC(
                          dueDate.getFullYear(),
                          dueDate.getMonth(),
                          dueDate.getDate(),
                        ) <
                        Date.UTC(
                          now.getFullYear(),
                          now.getMonth(),
                          now.getDate(),
                        );
                      return (
                        <button
                          key={activity._id}
                          onClick={() => {
                            setActiveSubTab("Activities");
                            setSelectedActivity(activity);
                          }}
                          className={classNames(
                            "flex flex-col items-start rounded-lg px-4 py-2 text-left text-sm font-medium ring-1 ring-inset transition-colors",
                            isPastDue
                              ? "bg-red-500/10 text-red-300 ring-red-500/20 hover:bg-red-500/20"
                              : "bg-orange-500/10 text-orange-300 ring-orange-500/20 hover:bg-orange-500/20",
                          )}
                        >
                          <span>{activity.title}</span>
                          <span className="mt-0.5 text-xs opacity-80">
                            Due: {formatDate(dueDate)}
                            {isPastDue ? " (overdue)" : ""}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}
            </div>

            {/* Timeline */}
            <div className="xl:col-span-1">
              <div className="xl:sticky xl:top-4">
                <Section title="Activity Timeline">
                  <nav aria-label="Progress">
                    <ol
                      role="list"
                      className="max-h-[calc(100vh-260px)] overflow-y-auto pr-2"
                    >
                      {timelineEvents.length === 0 ? (
                        <p className="text-sm text-gray-400">
                          No timeline events available.
                        </p>
                      ) : (
                        timelineEvents.map((event, eventIdx) => {
                          const status = getEventStatus(event, eventIdx);
                          const isLast = eventIdx === timelineEvents.length - 1;
                          return (
                            <li
                              key={event.id}
                              className={classNames(
                                !isLast ? "pb-8" : "",
                                "relative",
                              )}
                            >
                              {!isLast && (
                                <div
                                  aria-hidden="true"
                                  className={classNames(
                                    "absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5",
                                    status === "complete"
                                      ? "bg-orange-400"
                                      : "bg-gray-700",
                                  )}
                                />
                              )}
                              <div className="relative flex items-start">
                                <span className="flex h-9 items-center">
                                  {status === "complete" ? (
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full bg-orange-400">
                                      <CheckIcon className="size-5 text-gray-900" />
                                    </span>
                                  ) : status === "current" ? (
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-orange-400 bg-gray-800">
                                      <span className="size-2.5 rounded-full bg-orange-400" />
                                    </span>
                                  ) : (
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-600 bg-gray-800">
                                      <span className="size-2.5 rounded-full bg-transparent" />
                                    </span>
                                  )}
                                </span>
                                <span className="ml-4 flex min-w-0 flex-col">
                                  <span
                                    className={classNames(
                                      "text-sm font-semibold",
                                      status === "current"
                                        ? "text-orange-400"
                                        : status === "complete"
                                          ? "text-gray-100"
                                          : "text-gray-400",
                                    )}
                                  >
                                    {eventTitle(event)}
                                  </span>
                                  <span className="mt-0.5 text-sm text-gray-300">
                                    {event.description}
                                  </span>
                                  <span className="mt-0.5 text-xs text-gray-500">
                                    By {event.changedBy} ·{" "}
                                    {formatDate(event.timestamp)}
                                  </span>
                                </span>
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ol>
                  </nav>
                </Section>
              </div>
            </div>
          </div>
        )}

        {/* FILES */}
        {activeSubTab === "Files" && (
          <SharePointFilesTab
            entityId={opportunity._id}
            entityName={opportunity.title}
            entityType="Opportunity"
            sharePointFolderName={opportunity.sharePointFolderName}
            setEntity={(updatedOpportunity) =>
              setOpportunities((prev) =>
                prev.map((opp) =>
                  opp._id === opportunity._id ? updatedOpportunity : opp,
                ),
              )
            }
          />
        )}

        {/* PROPOSALS */}
        {activeSubTab === "Proposals" && (
          <Section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Proposals</h2>
              {!isEditingProposal && !isCreatingProposal && (
                <button
                  onClick={() => setIsCreatingProposal(true)}
                  className={btnPrimary}
                >
                  <PlusIcon className="size-4" />
                  Create Proposal
                </button>
              )}
            </div>
            {(isCreatingProposal || isEditingProposal) && (
              <ProposalForm
                opportunityId={opportunity._id}
                setProposals={setProposals}
                setOpportunities={setOpportunities}
                setIsCreatingProposal={setIsCreatingProposal}
                setIsEditingProposal={setIsEditingProposal}
                proposal={
                  isEditingProposal
                    ? proposals.find((p) => p._id === isEditingProposal)
                    : null
                }
                fetchProposals={fetchProposals}
              />
            )}
            <ProposalList
              proposals={proposals.filter(
                (prop) =>
                  (prop.opportunity?._id || prop.opportunity) ===
                  opportunity._id,
              )}
              setProposals={setProposals}
              setOpportunities={setOpportunities}
              opportunityId={opportunity._id}
              companyProfile={companyProfile}
              userEmail={userEmail}
              accounts={accounts}
              contacts={contacts}
              opportunities={[opportunity]}
              setIsEditingProposal={setIsEditingProposal}
              fetchProposals={fetchProposals}
            />
          </Section>
        )}

        {/* NOTES */}
        {activeSubTab === "Notes" && (
          <Section>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-white">Notes</h2>
              <div className="flex gap-2">
                <button onClick={toggleNoteSortOrder} className={btnSecondary}>
                  Sort:{" "}
                  {noteSortOrder === "desc" ? "Newest First" : "Oldest First"}
                </button>
                <button
                  onClick={() => setShowNoteForm(true)}
                  className={btnPrimary}
                >
                  <PlusIcon className="size-4" />
                  Add Note
                </button>
              </div>
            </div>

            {showNoteForm && (
              <form
                onSubmit={handleAddNote}
                className="mb-6 rounded-lg bg-gray-900 p-4 ring-1 ring-white/10"
              >
                <Label>New Note</Label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className={inputClass}
                  rows={4}
                  placeholder="Enter your note here..."
                />
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNoteForm(false);
                      setNoteContent("");
                    }}
                    className={btnSecondary}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={btnPrimary}>
                    Save Note
                  </button>
                </div>
              </form>
            )}

            {sortedNotes.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">
                No notes available.
              </p>
            ) : (
              <ul className="space-y-3">
                {sortedNotes.map((note, index) => (
                  <li
                    key={note._id || index}
                    className="rounded-lg bg-gray-900 p-4 ring-1 ring-white/10"
                  >
                    <p className="whitespace-pre-wrap text-sm text-gray-100">
                      {note.content}
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {note.createdBy?.displayName || "Unknown"} ·{" "}
                      {formatDate(note.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {/* ACTIVITIES */}
        {activeSubTab === "Activities" && (
          <Section>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Activities</h2>
              <button
                onClick={() => {
                  setShowActivityForm(true);
                  setIsEditingActivity(false);
                  setNewActivity({
                    title: "",
                    dueDate: "",
                    description: "",
                    method: "email",
                    assignedTo: getCurrentUserId(),
                  });
                }}
                className={btnPrimary}
              >
                <PlusIcon className="size-4" />
                Add Activity
              </button>
            </div>

            {showActivityForm && (
              <form
                onSubmit={handleAddActivity}
                className="mb-6 rounded-lg bg-gray-900 p-4 ring-1 ring-white/10"
              >
                <ActivityFields
                  value={newActivity}
                  onChange={setNewActivity}
                  userOptions={userOptions}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowActivityForm(false);
                      setNewActivity({
                        title: "",
                        dueDate: "",
                        description: "",
                        method: "email",
                        assignedTo: getCurrentUserId(),
                      });
                    }}
                    className={btnSecondary}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={btnPrimary}>
                    Save Activity
                  </button>
                </div>
              </form>
            )}

            {selectedActivity && (
              <div className="mb-6 rounded-lg bg-gray-900 p-5 ring-1 ring-orange-500/30">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-orange-400">
                    {selectedActivity.title}
                  </h3>
                  <div className="flex gap-2">
                    {!isEditingActivity && (
                      <button
                        onClick={() => handleEditActivity(selectedActivity)}
                        className="rounded-lg px-3 py-1.5 text-sm font-medium text-orange-400 hover:bg-orange-500/10"
                      >
                        Edit
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedActivity(null);
                        setIsEditingActivity(false);
                      }}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-400 hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                </div>

                {isEditingActivity ? (
                  <form onSubmit={handleSubmitEditActivity}>
                    <ActivityFields
                      value={editActivity}
                      onChange={setEditActivity}
                      userOptions={userOptions}
                      showStatus
                    />
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsEditingActivity(false)}
                        className={btnSecondary}
                      >
                        Cancel
                      </button>
                      <button type="submit" className={btnPrimary}>
                        Save Changes
                      </button>
                    </div>
                  </form>
                ) : (
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                    <Field label="Due Date">
                      {formatDate(selectedActivity.dueDate)}
                    </Field>
                    <Field label="Method">
                      <span className="capitalize">
                        {selectedActivity.method}
                      </span>
                    </Field>
                    <Field label="Status">
                      <select
                        value={selectedActivity.status}
                        onChange={(e) =>
                          handleUpdateActivity(selectedActivity._id, {
                            status: e.target.value,
                          })
                        }
                        className={classNames(inputClass, "max-w-xs")}
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </select>
                    </Field>
                    <Field label="Assigned To">
                      {selectedActivity.assignedTo?.displayName ||
                        selectedActivity.createdBy?.displayName ||
                        "Unknown"}
                    </Field>
                    <Field label="Description" className="md:col-span-2">
                      {selectedActivity.description}
                    </Field>
                    <Field label="Created By">
                      {selectedActivity.createdBy?.displayName || "Unknown"}
                    </Field>
                    <Field label="Created On">
                      {formatDate(selectedActivity.createdOn)}
                    </Field>
                  </dl>
                )}

                <div className="mt-5 flex justify-end border-t border-white/10 pt-4">
                  <button
                    onClick={() =>
                      showAlert(
                        "warning",
                        "Confirm Delete",
                        "Are you sure you want to delete this activity?",
                        {
                          onConfirm: () => {
                            handleDeleteActivity(selectedActivity._id);
                            closeAlert();
                          },
                          showCancel: true,
                        },
                      )
                    }
                    className={btnDanger}
                  >
                    <TrashIcon className="size-4" />
                    Delete Activity
                  </button>
                </div>
              </div>
            )}

            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              All Activities
            </h3>
            {activities.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">
                No activities available.
              </p>
            ) : (
              <ul className="space-y-2">
                {[...activities]
                  .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
                  .map((activity) => (
                    <li
                      key={activity._id}
                      className={classNames(
                        "cursor-pointer rounded-lg bg-gray-900 p-4 ring-1 transition-colors hover:bg-gray-700/60",
                        selectedActivity?._id === activity._id
                          ? "ring-orange-500/40"
                          : "ring-white/10",
                      )}
                      onClick={() => {
                        setSelectedActivity(activity);
                        setIsEditingActivity(false);
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-100">
                            {activity.title}
                          </p>
                          <p className="mt-0.5 truncate text-sm text-gray-400">
                            {activity.description}
                          </p>
                          <p className="mt-2 text-xs text-gray-500">
                            <span className="capitalize">
                              {activity.method}
                            </span>{" "}
                            · Due {formatDate(activity.dueDate)} · Created by{" "}
                            {activity.createdBy?.displayName || "Unknown"} ·
                            Assigned to{" "}
                            {activity.assignedTo?.displayName ||
                              activity.createdBy?.displayName ||
                              "Unknown"}
                          </p>
                        </div>
                        <Badge
                          tone={
                            activity.status === "pending" ? "yellow" : "green"
                          }
                        >
                          {activity.status.charAt(0).toUpperCase() +
                            activity.status.slice(1)}
                        </Badge>
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </Section>
        )}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
